<template>
  <div>
    <div class="d-flex flex-wrap align-center ga-2 mb-3">
      <v-spacer />
      <v-btn size="small" variant="tonal" color="tertiary" :disabled="!campaigns.length" @click="copyMarkdown">
        <v-icon start size="16">mdi-content-copy</v-icon>
        {{ $t("ncrm.emailMarketing.campaignsTab.copyMarkdown") }}
      </v-btn>
      <v-btn size="small" variant="tonal" color="tertiary" :disabled="!campaigns.length" @click="downloadJson">
        <v-icon start size="16">mdi-download-outline</v-icon>
        {{ $t("ncrm.emailMarketing.campaignsTab.downloadJson") }}
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="campaigns"
      :loading="loading"
      item-key="id"
      density="comfortable"
      class="elevation-0"
      :items-per-page="50"
    >
      <template #item.subject="{ item }">
        <span class="ncrm-em-campaigns__subject">{{ item.subject || "—" }}</span>
      </template>
      <template #item.sent_at="{ item }">
        <span class="text-no-wrap">
          <v-icon size="13" style="opacity: 0.38">mdi-clock-outline</v-icon>
          {{ formatDate(item.sent_at) }}
        </span>
      </template>
      <template #item.openRate="{ item }">{{ formatPercent(item.rates?.openRate) }}</template>
      <template #item.clickRate="{ item }">{{ formatPercent(item.rates?.clickRate) }}</template>
      <template #item.bounceRate="{ item }">{{ formatPercent(item.rates?.bounceRate) }}</template>
      <template #item.unsubRate="{ item }">{{ formatPercent(item.rates?.unsubRate) }}</template>
      <template #item.leadsAdded="{ item }">
        <v-chip v-if="item.leadsAdded > 0" size="x-small" color="success">
          {{ item.leadsAdded }}
        </v-chip>
        <span v-else class="text-medium-emphasis">0</span>
      </template>
      <template #item.dealsCount="{ item }">
        <span :class="{ 'font-weight-bold': item.dealsCount > 0 }">{{ item.dealsCount }}</span>
        <span v-if="item.contractsCount > 0" class="text-caption text-medium-emphasis">
          (+{{ item.contractsCount }} {{ $t("ncrm.emailMarketing.campaignsTab.contractsShort") }})
        </span>
      </template>
      <template #item.score="{ item }">
        <v-chip size="x-small" :color="scoreColor(item.score)">
          {{ item.score ?? "—" }}
        </v-chip>
        <v-icon v-if="item.atRisk" size="14" color="error" class="ml-1">mdi-alert-outline</v-icon>
        <v-tooltip v-if="item.metricsSource === 'events'" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-icon v-bind="tooltipProps" size="14" class="ml-1" style="opacity: 0.55">
              mdi-approximately-equal
            </v-icon>
          </template>
          {{ $t("ncrm.emailMarketing.estimatedFromEvents") }}
        </v-tooltip>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.emailMarketing.campaignsTab.empty") }}
        </v-alert>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  formatPercent,
  resolveScoreColor,
  buildCampaignsMarkdownExport,
  buildCampaignsJsonExport,
} from "@/Utile/ncrmEmailMarketing.mjs";

export default {
  name: "NcrmEmailMarketingCampaignsTab",
  props: {
    campaigns: { type: Array, default: () => [] },
    overview: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  emits: ["notify"],
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.subject"), key: "subject" },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.sentAt"), key: "sent_at" },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.delivered"), key: "delivered_count" },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.openRate"), key: "openRate", sortable: false },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.clickRate"), key: "clickRate", sortable: false },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.bounceRate"), key: "bounceRate", sortable: false },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.unsubRate"), key: "unsubRate", sortable: false },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.leads"), key: "leadsAdded" },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.deals"), key: "dealsCount" },
        { title: this.$t("ncrm.emailMarketing.campaignsTab.headers.score"), key: "score" },
      ];
    },
  },
  methods: {
    formatPercent,
    scoreColor: resolveScoreColor,
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      return `${dd}.${mm}.${date.getFullYear()}`;
    },
    async copyMarkdown() {
      const markdown = buildCampaignsMarkdownExport({
        campaigns: this.campaigns,
        overview: this.overview,
      });
      try {
        await navigator.clipboard.writeText(markdown);
        this.$emit("notify", {
          message: this.$t("ncrm.emailMarketing.campaignsTab.copySuccess"),
          color: "success",
        });
      } catch (error) {
        this.$emit("notify", {
          message: this.$t("ncrm.emailMarketing.campaignsTab.copyError"),
          color: "error",
        });
      }
    },
    downloadJson() {
      const json = buildCampaignsJsonExport({
        campaigns: this.campaigns,
        overview: this.overview,
      });
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `email-marketing-report-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.ncrm-em-campaigns__subject {
  font-weight: 600;
  color: var(--ncrm-color-text-strong);
}
</style>
